import type { Exercise } from "./schemas/exercise.js";
import type { AthleteTargets } from "./schemas/program.js";
import { findPinnedProgram, type ProgramRow } from "./programsList.js";
import type { TimedSessionResult } from "./progressTypes.js";
import { programWeeklyPrescription } from "./programPrescription.js";
import {
  computeWeeklyTargets,
  zoneFor,
  type TargetZone,
} from "./weeklyTargets.js";
import { weekWindowStart } from "./weekWindow.js";
import { DAY_MS } from "./freshness.js";
import { pickAuthoredText } from "./authoredText.js";
import type { Locale } from "./locale.js";

// CO-8 coach dashboard — the pure team-overview aggregation. Given each assigned
// athlete's already-fetched bundle (session-results + their catalog + their
// programs + display name/config, all read-only via the CO-11 coach gate), it
// derives ONE card per athlete and the KPI-strip counters, sorted attention-first.
//
// Adherence is DERIVED from computeWeeklyTargets' own tallies, not a parallel
// formula: the numerator (hitCount) and denominator (targetCount) below are
// read straight off `targets.onTargetCount`/`targets.targetCount` — the SAME
// uncapped pair the athlete's own Home meter shows, scored by the SAME
// isOnTarget(zone) predicate (weeklyTargets.ts: in-band OR over). Two
// invariants keep the coach from drifting off the athlete's Home meter
// (§CONSOLIDAMENTO "two calcs, two numbers"; CO-26 was exactly this drift,
// twice over): (1) one shared predicate decides "hit", never a hand-written
// condition per caller; (2) numerator and denominator always come from the
// SAME population — never the capped `targets.rows` (MAX_ROWS=8) paired with
// an uncapped total, which silently ceilings adherence below 100% for any
// athlete with >8 targeted muscles. Exposes an "adherence bar" as an
// actual-vs-target pair the host feeds straight into <TargetBar> (100 = full
// adherence).

// No session in this many days ⇒ the athlete reads as "stale" (a data fact the
// coach interprets — not a judgement JL makes).
const STALE_DAYS = 10;
// Adherence band around full (100%): ≥90% of targets hit reads "on track".
// Exported so the dashboard's <TargetBar> draws its band from the SAME numbers
// that score adherenceZone here — a second copy could silently disagree.
export const ADHERENCE_BAND_LOW = 90;
export const ADHERENCE_BAND_HIGH = 110;

export type CoachAthleteInput = {
  sub: string;
  name: string;
  weekStartDay?: number | undefined;
  currentProgramId?: string | undefined;
  sessions: TimedSessionResult[];
  catalog: ReadonlyArray<Exercise>;
  // A program another coach wrote comes as its summary (#332).
  programs: ReadonlyArray<ProgramRow>;
  // ME-3 — the athlete's own weekly targets (AthleteTargets). The adherence
  // score is against these now (the pinned program stays for the block name +
  // prescription). Absent/null → no scoreable target (degraded card); the
  // program's embedded targets are not read.
  athleteTargets?: AthleteTargets | null | undefined;
  // The session-results read failed. Every fact on a card comes from it, so
  // this athlete gets a "not loaded" row, never a "never trained" card.
  unavailable?: boolean | undefined;
};

export type CoachAthleteFlag =
  | "no-program"
  | "stale"
  | "under-target"
  | "on-track";

export type CoachAthleteCard = {
  sub: string;
  name: string;
  // The pinned "active block" program name, or null when nothing pinned.
  blockName: string | null;
  lastSessionAtMs: number | null;
  daysSinceLast: number | null;
  sessionsThisWeek: number;
  // % of targeted muscles hit (in-band or over). Null in degraded mode
  // (no scoreable target) or with no pinned program.
  adherencePct: number | null;
  adherenceZone: TargetZone | null;
  targetCount: number;
  // Targets on band or over, same uncapped population as targetCount.
  onTargetCount: number;
  flags: CoachAthleteFlag[];
  // Higher = needs the coach's attention sooner. Drives the list sort.
  priority: number;
};

export type CoachKpis = {
  assigned: number;
  activeThisWeek: number;
  attention: number;
  avgAdherencePct: number | null;
  sessionsThisWeek: number;
};

export type CoachUnavailableAthlete = { sub: string; name: string };

export type CoachDashboardData = {
  cards: CoachAthleteCard[];
  // `assigned` counts the whole roster; every other KPI counts `cards` only.
  kpis: CoachKpis;
  unavailable: CoachUnavailableAthlete[];
};

export const buildCoachAthleteCard = (
  a: CoachAthleteInput,
  nowMs: number,
  locale?: Locale,
): CoachAthleteCard => {
  // A summary names the block but has no sessions to prescribe from.
  const { row: pinnedRow, program: pinned } = findPinnedProgram(
    a.programs,
    a.currentProgramId,
  );
  const prescription = pinned
    ? programWeeklyPrescription(pinned, a.catalog)
    : null;
  const targets = computeWeeklyTargets(
    a.sessions,
    a.catalog,
    pinned,
    nowMs,
    prescription,
    { weekStartDay: a.weekStartDay },
    a.athleteTargets ?? null,
  );

  const lastSessionAtMs = a.sessions.reduce<number | null>(
    (acc, s) => (acc == null || s.startedAtMs > acc ? s.startedAtMs : acc),
    null,
  );
  const daysSinceLast =
    lastSessionAtMs == null
      ? null
      : Math.floor((nowMs - lastSessionAtMs) / DAY_MS);
  const weekStart = weekWindowStart(nowMs, a.weekStartDay);
  const sessionsThisWeek = a.sessions.filter(
    (s) => s.startedAtMs >= weekStart,
  ).length;

  // Adherence from the meter's own tallies — NOT from `targets.rows`, which
  // `weeklyTargets.ts` caps at MAX_ROWS (8) for the card's glanceability.
  // `onTargetCount`/`targetCount` are the uncapped numerator+denominator
  // computeWeeklyTargets already scores every targeted muscle into (same
  // isOnTarget predicate, same population): reading both from that one pair
  // keeps numerator and denominator over the SAME population by construction
  // — a numerator built off the capped `rows` while the denominator stayed
  // uncapped is the CO-26 bug again, one level down (a coach with >8 targeted
  // muscles could never reach 100% adherence). `anyUnder` follows the same
  // uncapped totals: with only three zones, "some row is under" ⇔ hitCount is
  // short of targetCount.
  const targetCount = targets?.targetCount ?? 0;
  const hitCount = targets?.onTargetCount ?? 0;
  const anyUnder = hitCount < targetCount;
  const adherencePct =
    targetCount > 0 ? Math.round((100 * hitCount) / targetCount) : null;
  const adherenceZone =
    adherencePct == null
      ? null
      : zoneFor(adherencePct, ADHERENCE_BAND_LOW, ADHERENCE_BAND_HIGH);

  const stale = daysSinceLast == null || daysSinceLast > STALE_DAYS;
  const flags: CoachAthleteFlag[] = [];
  if (!pinnedRow) flags.push("no-program");
  if (stale) flags.push("stale");
  if (anyUnder) flags.push("under-target");
  if (!stale && !anyUnder && targetCount > 0) flags.push("on-track");

  // Attention weight — additive so "stale AND under target" sorts above either
  // alone. on-track adds nothing (it's the calm state).
  const priority =
    (stale ? 3 : 0) + (anyUnder ? 2 : 0) + (!pinnedRow ? 1 : 0);

  return {
    sub: a.sub,
    name: a.name,
    blockName: pickAuthoredText(
      pinnedRow?.name,
      locale,
      pinnedRow?.authorLocale,
    ),
    lastSessionAtMs,
    daysSinceLast,
    sessionsThisWeek,
    adherencePct,
    adherenceZone,
    targetCount,
    onTargetCount: hitCount,
    flags,
    priority,
  };
};

const buildKpis = (
  cards: ReadonlyArray<CoachAthleteCard>,
  assigned: number,
): CoachKpis => {
  const activeThisWeek = cards.filter((c) => c.sessionsThisWeek > 0).length;
  const attention = cards.filter((c) => c.priority > 0).length;
  const sessionsThisWeek = cards.reduce((n, c) => n + c.sessionsThisWeek, 0);
  const scored = cards.filter((c) => c.adherencePct != null);
  const avgAdherencePct =
    scored.length > 0
      ? Math.round(
          scored.reduce((n, c) => n + (c.adherencePct ?? 0), 0) / scored.length,
        )
      : null;
  return {
    assigned,
    activeThisWeek,
    attention,
    avgAdherencePct,
    sessionsThisWeek,
  };
};

/** The dashboard from cards already built (a host keeps the last good card
 *  of an athlete whose read failed). `assigned` is the whole roster. */
export const assembleCoachDashboard = (
  cards: CoachAthleteCard[],
  unavailable: CoachUnavailableAthlete[],
  assigned: number,
): CoachDashboardData => {
  // Attention-first; tie-break by staleness (a null last-session, never
  // trained, sorts as most stale).
  cards.sort(
    (a, b) =>
      b.priority - a.priority ||
      (b.daysSinceLast ?? Number.MAX_SAFE_INTEGER) -
        (a.daysSinceLast ?? Number.MAX_SAFE_INTEGER),
  );
  return { cards, kpis: buildKpis(cards, assigned), unavailable };
};

export const buildCoachDashboard = (
  inputs: ReadonlyArray<CoachAthleteInput>,
  nowMs: number,
  // Reader's language (PE-21): the program's authored text is resolved
  // reader → author → any available. Omitted → the chain starts at the
  // author, never at English.
  locale?: Locale,
): CoachDashboardData => {
  const cards: CoachAthleteCard[] = [];
  const unavailable: CoachUnavailableAthlete[] = [];
  for (const a of inputs) {
    if (a.unavailable) unavailable.push({ sub: a.sub, name: a.name });
    else cards.push(buildCoachAthleteCard(a, nowMs, locale));
  }
  return assembleCoachDashboard(cards, unavailable, inputs.length);
};
